/** Shared Player full-screen loading. Ref-counted user-wait tokens. */

export function isUsableWebsiteLogoUrl(url) {
  const value = String(url || '').trim();
  if (!value) return false;
  if (value.startsWith('data:image/')) return true;
  if (value.startsWith('blob:')) return true;
  if (value.startsWith('/') && !value.startsWith('//')) return true;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export function withBrandingCacheParam(url, version) {
  const value = String(url || '').trim();
  if (!value) return '';
  if (value.startsWith('data:') || value.startsWith('blob:') || !version) return value;
  const token = encodeURIComponent(String(version));
  return `${value}${value.includes('?') ? '&' : '?'}v=${token}`;
}

/** Loader image: Website Management Loading Image only. Never logo, favicon, or the old lightning SVG. */
export function resolvePlayerLoadingSrc(loadingImageUrl, version = '') {
  if (!isUsableWebsiteLogoUrl(loadingImageUrl)) return '';
  return withBrandingCacheParam(String(loadingImageUrl).trim(), version);
}

export const PLAYER_PUBLIC_SITE_ID = 'WS0005';
const LOADING_IMAGE_CACHE_MAX = 2048;

export function loadingImageCacheKey(siteId = PLAYER_PUBLIC_SITE_ID) {
  return `ninet6.loadingImage.${String(siteId || PLAYER_PUBLIC_SITE_ID)}`;
}

export function extractPublicLoadingImageUrl(payload) {
  const root = payload && typeof payload === 'object' ? payload : {};
  const branding = root.data?.branding || root.branding || {};
  return String(branding?.loadingImage?.url || '').trim();
}

export function rememberPlayerLoadingSrc(loadingImageUrl, siteId = PLAYER_PUBLIC_SITE_ID) {
  const value = String(loadingImageUrl || '').trim();
  if (!isUsableWebsiteLogoUrl(value)) return;
  if (value.startsWith('data:') || value.startsWith('blob:') || value.length > LOADING_IMAGE_CACHE_MAX) return;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(loadingImageCacheKey(siteId), value);
  } catch {
    /* ignore quota */
  }
}

export function readCachedPlayerLoadingSrc(siteId = PLAYER_PUBLIC_SITE_ID) {
  try {
    if (typeof localStorage === 'undefined') return '';
    return resolvePlayerLoadingSrc(localStorage.getItem(loadingImageCacheKey(siteId)) || '');
  } catch {
    return '';
  }
}

export function paintHtmlBootLoadingImage(loadingImageUrl) {
  if (typeof document === 'undefined') return false;
  const src = resolvePlayerLoadingSrc(loadingImageUrl);
  const root = document.getElementById('ninet6-boot-loader');
  if (!root || !src) return false;
  let img = document.getElementById('ninet6-boot-loading-img');
  if (!img) {
    img = document.createElement('img');
    img.id = 'ninet6-boot-loading-img';
    img.alt = '';
    root.insertBefore(img, root.firstChild);
  }
  if (img.getAttribute('src') !== src) img.setAttribute('src', src);
  img.hidden = false;
  img.onerror = () => {
    img.hidden = true;
    img.removeAttribute('src');
    root.classList.remove('has-loading-image');
  };
  root.classList.add('has-loading-image');
  return true;
}
/** One session failsafe from game click until actual frameReady. Not fake success. */
export const GAME_SESSION_TIMEOUT_MS = 45000;
export const GAME_LAUNCH_TIMEOUT_MS = GAME_SESSION_TIMEOUT_MS;
export const GAME_IFRAME_TIMEOUT_MS = GAME_SESSION_TIMEOUT_MS;
export const GAME_LAUNCH_FAILED_MESSAGE = 'Game failed to load. Please try again.';
/** Stuck/error escape for page blocking tokens. Not a minimum display time. Game launch does not use this. */
export const BLOCKING_LOAD_STUCK_MS = 20000;

let seq = 0;
const tokens = new Set();
const stuckTimers = new Map();
const listeners = new Set();
let bootReady = false;
let gameSession = { url: '', loadingToken: 0, frameReady: false, generation: 0 };
let launchTimeoutId = 0;
let launchAbort = null;
let launchGeneration = 0;
let gameTokenReleases = 0;

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function notifyLaunchFailed(message) {
  try {
    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert(message);
    }
  } catch {
    /* ignore */
  }
}

export function subscribePlayerLoading(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPlayerLoadingCount() {
  return tokens.size;
}

export function isPlayerLoadingVisible() {
  if (gameSession.url && gameSession.frameReady) return false;
  return !bootReady || tokens.size > 0;
}

export function beginPlayerLoading(label, stuckMs = 0) {
  const id = ++seq;
  tokens.add(id);
  const ms = Number(stuckMs) || 0;
  if (ms > 0) {
    stuckTimers.set(
      id,
      setTimeout(() => {
        stuckTimers.delete(id);
        if (tokens.delete(id)) emit();
      }, ms),
    );
  }
  emit();
  return id;
}

export function endPlayerLoading(id) {
  if (id == null) return;
  const timer = stuckTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    stuckTimers.delete(id);
  }
  tokens.delete(id);
  emit();
}

export function isPlayerBootReady() {
  return bootReady;
}

export function dismissHtmlBootSplash() {
  try {
    const el = typeof document !== 'undefined' ? document.getElementById('ninet6-boot-loader') : null;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  } catch {
    /* ignore */
  }
}

export function markPlayerBootReady() {
  if (bootReady) {
    dismissHtmlBootSplash();
    return;
  }
  bootReady = true;
  dismissHtmlBootSplash();
  emit();
}

export function getGameSession() {
  return gameSession;
}

export function isUnusableGameFrame(el) {
  if (!el) return true;
  const assigned = String(el.getAttribute?.('src') || el.src || '');
  if (!assigned || assigned === 'about:blank' || assigned === 'about:srcdoc') return true;
  try {
    const href = String(el.contentWindow?.location?.href || '');
    if (!href || href === 'about:blank' || href === 'about:srcdoc') return true;
    const doc = el.contentDocument;
    if (doc) {
      const url = String(doc.URL || doc.location?.href || '');
      if (!url || url === 'about:blank' || url === 'about:srcdoc') return true;
      const body = doc.body;
      if (body && !body.childElementCount && !String(body.textContent || '').trim()) return true;
    }
  } catch {
    return false;
  }
  return false;
}

export function isGameReadyMessageType(payload) {
  const kind = typeof payload === 'string'
    ? payload
    : String(payload?.type || payload?.event || payload?.action || '');
  return /^(ready|loaded|gameReady|game-ready|iframeReady)$/i.test(kind.trim());
}

export function isTrustedGameFrameMessage(ev, iframeEl) {
  if (!ev || !iframeEl) return false;
  if (ev.source !== iframeEl.contentWindow) return false;
  if (!ev.origin || ev.origin === 'null') return false;
  return isGameReadyMessageType(ev.data);
}

export function clearGameLaunchTimeout() {
  if (launchTimeoutId) {
    clearTimeout(launchTimeoutId);
    launchTimeoutId = 0;
  }
}

export function hasGameSessionFailsafe() {
  return launchTimeoutId !== 0;
}

function runLaunchAbort() {
  const fn = launchAbort;
  launchAbort = null;
  if (!fn) return;
  try {
    fn();
  } catch {
    /* ignore */
  }
}

export function setGameLaunchAbort(fn) {
  launchAbort = typeof fn === 'function' ? fn : null;
}

function resetGameSessionFields() {
  gameSession = {
    url: '',
    loadingToken: 0,
    frameReady: false,
    generation: gameSession.generation,
  };
}

export function beginGameLaunch(options = {}) {
  clearGameLaunchTimeout();
  runLaunchAbort();
  const prior = gameSession.loadingToken;
  if (prior) endPlayerLoading(prior);
  const loadingToken = beginPlayerLoading('game-launch', 0);
  const generation = ++launchGeneration;
  const ms = Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : GAME_SESSION_TIMEOUT_MS;
  gameSession = { url: '', loadingToken, frameReady: false, generation };
  launchTimeoutId = setTimeout(() => {
    launchTimeoutId = 0;
    if (gameSession.generation !== generation) return;
    if (gameSession.frameReady) return;
    if (!gameSession.loadingToken && !gameSession.url) return;
    const msg = failGameSession(GAME_LAUNCH_FAILED_MESSAGE);
    notifyLaunchFailed(msg);
  }, ms);
  emit();
  return { loadingToken, generation };
}

export function isCurrentGameLaunch(token, generation) {
  return (
    !!token
    && gameSession.loadingToken === token
    && gameSession.generation === generation
    && !gameSession.frameReady
  );
}

export function openGameSession({ url, loadingToken }) {
  const token = loadingToken || gameSession.loadingToken;
  gameSession = {
    url: String(url || ''),
    loadingToken: token || 0,
    frameReady: false,
    generation: gameSession.generation,
  };
  emit();
}

export function markGameFrameReady() {
  const url = String(gameSession.url || '');
  if (!url || /^about:(blank|srcdoc)$/i.test(url)) return;
  clearGameLaunchTimeout();
  if (gameSession.frameReady) return;
  gameSession = { ...gameSession, frameReady: true };
  emit();
}

function finishGameSession() {
  clearGameLaunchTimeout();
  runLaunchAbort();
  const token = gameSession.loadingToken;
  resetGameSessionFields();
  if (token) {
    gameTokenReleases += 1;
    endPlayerLoading(token);
  } else {
    emit();
  }
}

export function closeGameSession() {
  finishGameSession();
}

export function failGameSession(message) {
  const idle = !gameSession.loadingToken && !gameSession.url && !launchTimeoutId && !launchAbort;
  if (!idle) finishGameSession();
  return message || GAME_LAUNCH_FAILED_MESSAGE;
}

export function getGameTokenReleaseCount() {
  return gameTokenReleases;
}

/** Test helper only. */
export function __resetPlayerLoadingForTests() {
  clearGameLaunchTimeout();
  runLaunchAbort();
  stuckTimers.forEach((timer) => clearTimeout(timer));
  stuckTimers.clear();
  tokens.clear();
  seq = 0;
  bootReady = false;
  launchGeneration = 0;
  gameTokenReleases = 0;
  gameSession = { url: '', loadingToken: 0, frameReady: false, generation: 0 };
  emit();
}
